/**
 * SONDA DE DIAGNÓSTICO (cliente) — canais extras pro bug
 * "Rendered more hooks than during the previous render"
 * (em produção: "Minified React error #310").
 *
 * Roda DEPOIS do HTML e ANTES da hidratação do React, então é o ponto mais
 * cedo em que dá pra instalar listeners no cliente.
 *
 * Por que aqui e não num handler de root: o Next 16 fixa os
 * `onUncaughtError`/`onCaughtError` do `hydrateRoot` (app-index.js) — não há
 * API pra substituí-los. O que sobra é:
 *   (a) `window.error` — o Next reporta erro NÃO capturado via
 *       reportGlobalError, que dispara um evento de erro na window;
 *   (b) breadcrumb de navegação — o erro é intermitente e sobrevive à
 *       navegação, então saber QUAL transição estava em voo quando ele
 *       nasceu vale mais que o stack sozinho.
 *
 * IMPORTANTE (achado da caça de 05/08/2026): pro caso já investigado, o (a) é
 * o ÚNICO canal que enxerga o erro. O throw acontece dentro do <Router> do
 * Next, que fica acima de todo error boundary da aplicação — nenhum error.tsx
 * nem o HooksProbeBoundary chega a ser chamado, e por isso também não existe
 * componentStack. Em bateria Playwright, o equivalente é `page.on("pageerror")`.
 *
 * Tudo dentro de try/catch: instrumentação nunca pode derrubar o app.
 */

const PROBE_ENABLED =
  process.env.NODE_ENV !== "production" ||
  process.env.NEXT_PUBLIC_HOOKS_PROBE === "1";

/**
 * Em dev a mensagem vem por extenso; em build de produção o React manda só
 * "Minified React error #310" (mais hooks) / "#300" (menos hooks). Os dois
 * padrões precisam passar, senão a sonda fica cega justamente no build que
 * interessa checar.
 *
 * As frases são casadas INTEIRAS de propósito. A alternativa nua `hook` que
 * havia aqui casava com "webhook" — e o app fala com webhook o tempo todo
 * (control plane, ZapSign, Stripe). Qualquer erro de cliente citando webhook
 * entrava como `[HOOKS-PROBE:window]`, que é exatamente o marcador usado como
 * fonte de verdade pra contar ocorrências do bug nas baterias Playwright.
 */
const HOOK_ERROR_RE =
  /Rendered (more|fewer) hooks|Invalid hook call|Minified React error #3(00|10)\b/i;

if (PROBE_ENABLED && typeof window !== "undefined") {
  try {
    window.addEventListener("error", (event) => {
      const err = event.error;
      const msg = err?.message ?? event.message ?? "";
      // Só o que interessa — o console de dev já é barulhento demais.
      if (!HOOK_ERROR_RE.test(String(msg))) return;
      console.error(
        "[HOOKS-PROBE:window]",
        `url=${window.location.pathname + window.location.search}`,
        `w=${window.innerWidth}`,
        `msg=${msg}`,
        "\n--- errorStack ---",
        err?.stack ?? "(sem stack)",
      );
    });
  } catch {
    /* instrumentação nunca quebra o app */
  }
}

/** Breadcrumb: marca a transição em voo pra localizar o passo que gerou o erro. */
export function onRouterTransitionStart(
  url: string,
  navigationType: "push" | "replace" | "traverse",
) {
  if (!PROBE_ENABLED) return;
  try {
    console.log(
      `[HOOKS-PROBE:nav] ${navigationType} -> ${url} (de ${window.location.pathname}, w=${window.innerWidth})`,
    );
  } catch {
    /* idem */
  }
}
